import React, { useEffect, useState, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Settings, QrCode, RefreshCw, CheckCircle, XCircle, Power,
  Server, Wifi, Link2, Copy, Check, Users, Phone, Shield, AlertTriangle, Zap, Plus, Trash2
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Switch } from '@/components/ui/switch';

const productColors = {
  produto1: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  produto2: 'bg-blue-100 text-blue-700 border-blue-200',
  produto3: 'bg-purple-100 text-purple-700 border-purple-200',
  produto4: 'bg-amber-100 text-amber-700 border-amber-200',
  disparo: 'bg-red-100 text-red-700 border-red-200',
};

function InstanceCard({ instance, isAdmin, onConnect, onDisconnect, onSetupWebhook, onRefresh, webhookUrl }) {
  const [connecting, setConnecting] = useState(false);
  const [creatingInstance, setCreatingInstance] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const isConnected = instance.connected === true;

  const handleCreateAndConnect = async () => {
    setCreatingInstance(true);
    try {
      const createRes = await api.createEvolutionInstance(instance.instance_name);
      toast.success('Instancia criada!');
      if (createRes.data?.qrcode) {
        setQrCode(createRes.data.qrcode);
        startPolling();
        setCreatingInstance(false);
        return;
      }
      await new Promise(r => setTimeout(r, 2000));
      await fetchQR();
    } catch (err) {
      const msg = err?.response?.data?.detail || '';
      if (msg.includes('already') || msg.includes('exists')) {
        toast.info('Instancia ja existe. Gerando QR Code...');
        await new Promise(r => setTimeout(r, 1000));
        await fetchQR();
      } else {
        toast.error('Erro: ' + (msg || 'Verifique as configuracoes'));
      }
    } finally { setCreatingInstance(false); }
  };

  const fetchQR = async () => {
    setConnecting(true);
    try {
      const res = await api.getEvolutionQRCode(instance.instance_name);
      if (res.data?.qrcode) {
        setQrCode(res.data.qrcode);
        toast.info('Escaneie o QR Code com seu WhatsApp');
        startPolling();
      } else {
        toast.error('QR Code nao disponivel. Tente novamente.');
        setConnecting(false);
      }
    } catch { toast.error('Erro ao gerar QR Code'); setConnecting(false); }
  };

  const startPolling = () => {
    const interval = setInterval(async () => {
      try {
        const res = await api.getEvolutionStatus(instance.instance_name);
        if (res.data?.connected) {
          clearInterval(interval);
          setQrCode(null);
          setConnecting(false);
          toast.success(`${instance.instance_name} conectado!`);
          onRefresh();
        }
      } catch {}
    }, 5000);
    setTimeout(() => { clearInterval(interval); setConnecting(false); setQrCode(null); }, 120000);
  };

  const copyWH = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="p-5" data-testid={`instance-${instance.instance_name}-card`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-full ${isConnected ? 'bg-emerald-100' : 'bg-slate-100'}`}>
            <Server className={`w-5 h-5 ${isConnected ? 'text-emerald-600' : 'text-slate-400'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900">{instance.instance_name}</h3>
              <Badge className={`text-xs px-2 py-0.5 border ${productColors[instance.product] || 'bg-slate-100 text-slate-600'}`}>
                {instance.product_label}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Produto: {instance.product_label}
              {instance.phone && (
                <span className="ml-2 font-medium text-slate-700">
                  | Tel: +{instance.phone}
                </span>
              )}
              {instance.unmapped && (
                <span className="ml-2 font-medium text-amber-600">
                  | Nao mapeada
                </span>
              )}
            </p>
          </div>
        </div>
        {isConnected ? (
          <Badge className="bg-emerald-100 text-emerald-700 px-2.5 py-1 text-xs"><CheckCircle className="w-3 h-3 mr-1" /> Conectado</Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700 px-2.5 py-1 text-xs"><XCircle className="w-3 h-3 mr-1" /> Desconectado</Badge>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {!isConnected && isAdmin && (
          <Button size="sm" onClick={handleCreateAndConnect} disabled={connecting || creatingInstance} className="bg-emerald-600 hover:bg-emerald-700">
            {(connecting || creatingInstance) ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <QrCode className="w-4 h-4 mr-1" />}
            {creatingInstance ? 'Criando...' : connecting ? 'Conectando...' : 'Conectar'}
          </Button>
        )}
        {isConnected && isAdmin && (
          <Button variant="destructive" size="sm" onClick={() => onDisconnect(instance.instance_name)}>
            <Power className="w-4 h-4 mr-1" /> Desconectar
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={() => onSetupWebhook(instance.instance_name)}>
            <Link2 className="w-4 h-4 mr-1" /> Config. Webhook
          </Button>
        )}
      </div>

      {/* QR Code */}
      {qrCode && (
        <div className="mt-4 text-center p-4 bg-slate-50 rounded-lg">
          <QrCode className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <h4 className="text-sm font-semibold text-slate-900 mb-1">Escaneie o QR Code</h4>
          <p className="text-xs text-slate-600 mb-3">WhatsApp &gt; Configuracoes &gt; Aparelhos conectados</p>
          <div className="bg-white p-3 rounded-lg inline-block shadow-lg border">
            <img src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`} alt="QR Code" className="w-48 h-48" />
          </div>
          <p className="text-xs text-slate-400 mt-2">Expira em 2 minutos</p>
        </div>
      )}

      {/* Webhook info */}
      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2">
          <code className="text-xs text-slate-600 flex-1 truncate bg-white px-2 py-1 rounded border">{webhookUrl}</code>
          <Button variant="ghost" size="sm" onClick={copyWH}>
            {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function WhatsAppConfigPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  const [instances, setInstances] = useState([]);
  const [apiConfigured, setApiConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState([]);
  const [protection, setProtection] = useState(null);
  const [savingProtection, setSavingProtection] = useState(false);
  const [showApiConfig, setShowApiConfig] = useState(false);
  const [apiForm, setApiForm] = useState({ api_url: '', api_key: '', instance_name: '' });
  const [showAddInstance, setShowAddInstance] = useState(false);
  const [newInstance, setNewInstance] = useState({ instance_name: '', product: 'produto1' });
  const [stats, setStats] = useState({ connected_count: 0, total_count: 0, produto1_count: 0, produto2_count: 0, produto3_count: 0, produto4_count: 0, disparo_count: 0 });

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/evolution/webhook`;

  const loadInstances = useCallback(async () => {
    try {
      const res = await api.getAllInstances();
      const data = res.data;
      setInstances(data.instances || []);
      setApiConfigured(data.api_configured || false);
      setStats({
        connected_count: data.connected_count || 0,
        total_count: data.total_count || 0,
        produto1_count: data.produto1_count || 0,
        produto2_count: data.produto2_count || 0,
        produto3_count: data.produto3_count || 0,
        produto4_count: data.produto4_count || 0,
        disparo_count: data.disparo_count || 0,
      });
    } catch {
      setInstances([]);
      setApiConfigured(false);
    } finally { setLoading(false); }
  }, []);

  const loadOperators = useCallback(async () => {
    try { const res = await api.getOperators(); setOperators(res.data || []); } catch {}
  }, []);

  const loadProtection = useCallback(async () => {
    try { const res = await api.getProtectionSettings(); setProtection(res.data); } catch {}
  }, []);

  useEffect(() => { loadInstances(); loadOperators(); loadProtection(); }, [loadInstances, loadOperators, loadProtection]);

  const handleSaveApiConfig = async (e) => {
    e.preventDefault();
    try {
      await api.saveEvolutionConfig(apiForm);
      toast.success('API Evolution configurada!');
      setShowApiConfig(false);
      setApiForm({ api_url: '', api_key: '', instance_name: '' });
      await loadInstances();
    } catch { toast.error('Erro ao salvar configuracao'); }
  };

  const handleDisconnect = async (instanceName) => {
    if (!window.confirm(`Desconectar ${instanceName}?`)) return;
    try { await api.logoutEvolution(instanceName); toast.success('Desconectado'); loadInstances(); } catch { toast.error('Erro'); }
  };

  const handleSetupWebhook = async (instanceName) => {
    try {
      await api.setupEvolutionWebhook(webhookUrl, instanceName);
      toast.success(`Webhook configurado para ${instanceName}!`);
    } catch { toast.error('Erro ao configurar webhook'); }
  };

  const handleAddInstance = async (e) => {
    e.preventDefault();
    if (!newInstance.instance_name.trim()) { toast.error('Nome obrigatorio'); return; }
    try {
      await api.addInstanceMapping(newInstance.instance_name.trim(), newInstance.product);
      toast.success(`Instancia ${newInstance.instance_name} adicionada!`);
      setShowAddInstance(false);
      setNewInstance({ instance_name: '', product: 'produto1' });
      await loadInstances();
    } catch { toast.error('Erro ao adicionar'); }
  };

  const handleRemoveInstance = async (instanceName) => {
    if (!window.confirm(`Remover instancia ${instanceName} do mapeamento?`)) return;
    try {
      await api.removeInstanceMapping(instanceName);
      toast.success('Removida');
      await loadInstances();
    } catch { toast.error('Erro'); }
  };

  const handleSaveProtection = async () => {
    setSavingProtection(true);
    try {
      await api.updateProtectionSettings({
        enabled: protection.enabled,
        cooldown_minutes: protection.cooldown_minutes,
        min_delay_seconds: protection.min_delay_seconds,
        max_delay_seconds: protection.max_delay_seconds,
        warmup_enabled: protection.warmup_enabled,
        max_daily_limit: protection.max_daily_limit,
      });
      toast.success('Protecao salva!');
      loadProtection();
    } catch { toast.error('Erro ao salvar'); }
    finally { setSavingProtection(false); }
  };

  const handleResetWarmup = async () => {
    if (!window.confirm('Reiniciar warm-up?')) return;
    try { await api.resetWarmup(); toast.success('Warm-up reiniciado!'); loadProtection(); } catch { toast.error('Erro'); }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center"><RefreshCw className="w-8 h-8 animate-spin text-emerald-600" /></main>
      </div>
    );
  }

  const inboundInstances = instances.filter(i => ['produto1', 'produto2', 'produto3', 'produto4'].includes(i.product));
  const disparoInstances = instances.filter(i => i.product === 'disparo');
  const unmappedInstances = instances.filter(i => i.unmapped === true);

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="whatsapp-config-title">
                Central WhatsApp
              </h1>
              <p className="text-slate-600 text-sm mt-1">Gerencie todas as instancias, conexoes e configuracoes</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadInstances} data-testid="refresh-all-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
              </Button>
              {isAdmin && (
                <>
                  <Dialog open={showApiConfig} onOpenChange={setShowApiConfig}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="api-config-btn">
                        <Settings className="w-4 h-4 mr-1" /> API Config
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Configurar Evolution API</DialogTitle></DialogHeader>
                      <form onSubmit={handleSaveApiConfig} className="space-y-4">
                        <div><Label>URL da API</Label><Input placeholder="http://seu-ip:8080" value={apiForm.api_url} onChange={(e) => setApiForm({ ...apiForm, api_url: e.target.value })} required /></div>
                        <div><Label>API Key</Label><Input type="password" placeholder="Sua chave de API" value={apiForm.api_key} onChange={(e) => setApiForm({ ...apiForm, api_key: e.target.value })} required /></div>
                        <div><Label>Instancia Principal</Label><Input placeholder="principal" value={apiForm.instance_name} onChange={(e) => setApiForm({ ...apiForm, instance_name: e.target.value })} required /></div>
                        <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Salvar</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={showAddInstance} onOpenChange={setShowAddInstance}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-instance-btn">
                        <Plus className="w-4 h-4 mr-1" /> Nova Instancia
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Adicionar Instancia</DialogTitle></DialogHeader>
                      <form onSubmit={handleAddInstance} className="space-y-4">
                        <div>
                          <Label>Nome da Instancia</Label>
                          <Input placeholder="Ex: serum5, dores3" value={newInstance.instance_name} onChange={(e) => setNewInstance({ ...newInstance, instance_name: e.target.value })} required />
                        </div>
                        <div>
                          <Label>Produto</Label>
                          <Select value={newInstance.product} onValueChange={(v) => setNewInstance({ ...newInstance, product: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="produto1">Produto 1</SelectItem>
                              <SelectItem value="produto2">Produto 2</SelectItem>
                              <SelectItem value="produto3">Produto 3</SelectItem>
                              <SelectItem value="produto4">Produto 4</SelectItem>
                              <SelectItem value="disparo">Disparo</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Adicionar</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          </div>

          {/* Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 border-l-4 border-l-emerald-500" data-testid="summary-connection">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stats.connected_count > 0 ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                  <Wifi className={`w-5 h-5 ${stats.connected_count > 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Conexoes</p>
                  <p className="text-sm font-bold text-slate-700">{stats.connected_count}/{stats.total_count} online</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-l-4 border-l-emerald-500" data-testid="summary-inbound">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100"><Phone className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Recebimento</p>
                  <p className="text-sm font-bold text-emerald-700">{inboundInstances.length} instancias</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-l-4 border-l-red-500" data-testid="summary-disparo">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100"><Phone className="w-5 h-5 text-red-600" /></div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Disparo</p>
                  <p className="text-sm font-bold text-red-700">{disparoInstances.length} instancias</p>
                </div>
              </div>
            </Card>
          </div>

          {!apiConfigured && isAdmin && (
            <Card className="p-6 border-2 border-dashed border-amber-300 bg-amber-50" data-testid="api-not-configured">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <div>
                  <h3 className="font-semibold text-amber-800">Evolution API nao configurada</h3>
                  <p className="text-sm text-amber-700 mt-1">Clique em "API Config" acima para configurar a URL e chave da API.</p>
                </div>
              </div>
            </Card>
          )}

          {/* Instancias de Recebimento (Inbound) */}
          {inboundInstances.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs px-2 py-1 border">Recebimento de Leads</Badge>
                <span className="text-sm text-slate-500">{inboundInstances.filter(i => i.connected).length}/{inboundInstances.length} conectadas</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {inboundInstances.map(inst => (
                  <div key={inst.instance_name} className="relative">
                    <InstanceCard
                      instance={inst}
                      isAdmin={isAdmin}
                      onConnect={() => {}}
                      onDisconnect={handleDisconnect}
                      onSetupWebhook={handleSetupWebhook}
                      onRefresh={loadInstances}
                      webhookUrl={webhookUrl}
                    />
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 text-slate-400 hover:text-red-500"
                        onClick={() => handleRemoveInstance(inst.instance_name)}
                        data-testid={`remove-${inst.instance_name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instancias de Disparo (Outbound) */}
          {disparoInstances.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs px-2 py-1 border">Disparo em Massa</Badge>
                <span className="text-sm text-slate-500">{disparoInstances.filter(i => i.connected).length}/{disparoInstances.length} conectadas</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {disparoInstances.map(inst => (
                  <div key={inst.instance_name} className="relative">
                    <InstanceCard
                      instance={inst}
                      isAdmin={isAdmin}
                      onConnect={() => {}}
                      onDisconnect={handleDisconnect}
                      onSetupWebhook={handleSetupWebhook}
                      onRefresh={loadInstances}
                      webhookUrl={webhookUrl}
                    />
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 text-slate-400 hover:text-red-500"
                        onClick={() => handleRemoveInstance(inst.instance_name)}
                        data-testid={`remove-${inst.instance_name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instancias nao mapeadas (auto-descobertas) */}
          {unmappedInstances.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs px-2 py-1 border">Novas Instancias</Badge>
                <span className="text-sm text-slate-500">{unmappedInstances.length} detectadas na Evolution API</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {unmappedInstances.map(inst => (
                  <div key={inst.instance_name} className="relative">
                    <InstanceCard
                      instance={inst}
                      isAdmin={isAdmin}
                      onConnect={() => {}}
                      onDisconnect={handleDisconnect}
                      onSetupWebhook={handleSetupWebhook}
                      onRefresh={loadInstances}
                      webhookUrl={webhookUrl}
                    />
                    {isAdmin && (
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                          onClick={async () => {
                            try {
                              await api.addInstanceMapping(inst.instance_name, 'produto1');
                              toast.success(`${inst.instance_name} mapeada como Recebimento!`);
                              loadInstances();
                            } catch { toast.error('Erro'); }
                          }}
                          data-testid={`map-inbound-${inst.instance_name}`}
                        >
                          Recebimento
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                          onClick={async () => {
                            try {
                              await api.addInstanceMapping(inst.instance_name, 'disparo');
                              toast.success(`${inst.instance_name} mapeada como Disparo!`);
                              loadInstances();
                            } catch { toast.error('Erro'); }
                          }}
                          data-testid={`map-disparo-${inst.instance_name}`}
                        >
                          Disparo
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {instances.length === 0 && apiConfigured && (
            <Card className="p-8 text-center" data-testid="no-instances">
              <Server className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-600 mb-1">Nenhuma instancia configurada</h3>
              <p className="text-sm text-slate-500">Clique em "Nova Instancia" para adicionar.</p>
            </Card>
          )}

          {/* Protecao Anti-Banimento */}
          {isAdmin && protection && (
            <Card className="p-6 border-l-4 border-l-orange-400" data-testid="protection-panel">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-orange-600" />
                <h3 className="font-semibold text-slate-900">Protecao Anti-Banimento</h3>
                <Badge className={`text-xs ml-auto ${protection.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                  {protection.enabled ? 'Ativa' : 'Desativada'}
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-xs text-slate-500 uppercase font-medium">Mensagens Hoje</p>
                  <p className="text-lg font-bold text-slate-800">{protection.messages_today} <span className="text-sm font-normal text-slate-400">/ {protection.current_daily_limit}</span></p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-xs text-slate-500 uppercase font-medium">Dia do Warm-up</p>
                  <p className="text-lg font-bold text-slate-800">{protection.warmup_day + 1} <span className="text-sm font-normal text-slate-400">/ {protection.warmup_total_days}</span></p>
                </div>
                <div className={`p-3 rounded-lg border ${protection.in_cooldown ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-100'}`}>
                  <p className="text-xs text-slate-500 uppercase font-medium">Cooldown</p>
                  <p className={`text-lg font-bold ${protection.in_cooldown ? 'text-orange-600' : 'text-emerald-600'}`}>
                    {protection.in_cooldown ? `${protection.cooldown_remaining_minutes} min restante` : 'Liberado'}
                  </p>
                </div>
              </div>

              {protection.in_cooldown && (
                <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200 mb-4">
                  <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0" />
                  <p className="text-sm text-orange-700">Mensagens automaticas pausadas durante o cooldown pos-conexao.</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Protecao Ativa</p>
                    <p className="text-xs text-slate-500">Limita envios automaticos (Sofia, saudacao, follow-ups).</p>
                  </div>
                  <Switch checked={protection.enabled} onCheckedChange={(v) => setProtection({ ...protection, enabled: v })} data-testid="protection-toggle" />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Warm-up Progressivo</p>
                    <p className="text-xs text-slate-500">Aumenta limite diario de msgs automaticas (50 a 250)</p>
                  </div>
                  <Switch checked={protection.warmup_enabled} onCheckedChange={(v) => setProtection({ ...protection, warmup_enabled: v })} data-testid="warmup-toggle" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Cooldown pos-conexao (min)</Label>
                    <Input type="number" min={0} max={60} value={protection.cooldown_minutes} onChange={(e) => setProtection({ ...protection, cooldown_minutes: parseInt(e.target.value) || 0 })} data-testid="cooldown-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Delay minimo (seg)</Label>
                    <Input type="number" min={0} max={120} value={protection.min_delay_seconds} onChange={(e) => setProtection({ ...protection, min_delay_seconds: parseInt(e.target.value) || 0 })} data-testid="min-delay-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Delay maximo (seg)</Label>
                    <Input type="number" min={0} max={300} value={protection.max_delay_seconds} onChange={(e) => setProtection({ ...protection, max_delay_seconds: parseInt(e.target.value) || 0 })} data-testid="max-delay-input" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Limite diario maximo</Label>
                  <Input type="number" min={10} max={1000} value={protection.max_daily_limit} onChange={(e) => setProtection({ ...protection, max_daily_limit: parseInt(e.target.value) || 200 })} data-testid="daily-limit-input" />
                </div>

                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveProtection} disabled={savingProtection} className="bg-orange-600 hover:bg-orange-700" data-testid="save-protection-btn">
                    {savingProtection ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
                    Salvar Protecao
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleResetWarmup} data-testid="reset-warmup-btn">
                    <Zap className="w-4 h-4 mr-1" /> Reiniciar Warm-up
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Operadores */}
          <Card className="p-6" data-testid="operators-panel">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-slate-900">Operadores no Round-Robin</h3>
              <Badge className="bg-blue-50 text-blue-700 text-xs ml-auto">{operators.length} operadores</Badge>
            </div>
            <p className="text-sm text-slate-500 mb-4">Leads sao distribuidos automaticamente entre os operadores abaixo.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {operators.map((op, i) => (
                <div key={op.id || i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold">
                    {op.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{op.name}</p>
                    <p className="text-xs text-slate-500 truncate">{op.email}</p>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-600 text-xs shrink-0">Ativo</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
